/**
 * The memory panel — variant A's right column (#42 §6).
 *
 * Every write from a test or from the owner, newest first. FAILURE ROWS
 * ARE ALWAYS PRESENT and coloured: a belief that did not hold is the most
 * expensive thing this system learns, and a memory that showed only
 * confirmations would be a highlight reel, not a memory.
 *
 * `counts_by_kind` describes the WHOLE memory (a `kinds=` filter narrows
 * `items` only), so the header count is read from there, not from the
 * length of the page on screen.
 */

import { Badge, BlockStack, Card, Text } from '@shopify/polaris';

import type {
  DecidedItem,
  MemoryEntry,
  MemoryResponse,
} from '../../../types/agentMind';
import styles from '../workspace.module.css';

function MemoryRow({ entry }: { entry: MemoryEntry }) {
  const failure = entry.kind_class === 'failure';
  return (
    <div
      className={`${styles.memoryRow} ${failure ? styles.memoryRowFailure : ''}`}
      data-testid={`memory-row-${entry.id}`}
      data-kind-class={entry.kind_class}
    >
      <span className={styles.mono}>
        {new Date(entry.at).toLocaleDateString()}
      </span>
      <div>
        <div>
          <Badge tone={failure ? 'critical' : 'success'}>{entry.kind}</Badge>{' '}
          <span
            className={`${styles.delta} ${
              entry.delta >= 0 ? styles.deltaUp : styles.deltaDown
            }`}
          >
            {entry.delta >= 0 ? '+' : ''}
            {entry.delta.toFixed(2)}
          </span>
        </div>
        <Text as="p">{entry.statement}</Text>
        {entry.note ? (
          <div className={styles.subtle}>{entry.note}</div>
        ) : null}
      </div>
    </div>
  );
}

export function MemoryPanel({
  memory,
  decided,
}: {
  memory: MemoryResponse;
  decided: DecidedItem[];
}) {
  const beliefCount = Object.values(memory.counts_by_kind ?? {}).reduce(
    (total, n) => total + n,
    0
  );

  return (
    <Card>
      <BlockStack gap="300">
        <div data-testid="memory-panel">
          <Text as="h2" variant="headingMd">
            {`Memory · ${beliefCount} beliefs · every write from a test or from you · newest first`}
          </Text>
        </div>

        {memory.items.length === 0 ? (
          <Text as="p" tone="subdued">
            Nothing has been written to this agent’s memory yet.
          </Text>
        ) : (
          memory.items.map((entry) => (
            <MemoryRow key={entry.id} entry={entry} />
          ))
        )}

        {memory.lanes?.underpowered_available ? (
          <div className={styles.subtle} data-testid="underpowered-lane">
            Some reads were underpowered — they are recorded, and they did
            not move confidence.
          </div>
        ) : null}

        <div data-testid="decided-lane">
          <Text as="h3" variant="headingSm">
            Decided
          </Text>
          {decided.length === 0 ? (
            <Text as="p" tone="subdued">
              Nothing has been declined or expired yet.
            </Text>
          ) : (
            decided.map((item) => (
              <div key={item.proposal_id} className={styles.memoryRow}>
                <span className={styles.mono}>
                  {item.decided_at
                    ? new Date(item.decided_at).toLocaleDateString()
                    : '—'}
                </span>
                <div>
                  <Text as="p">{item.title}</Text>
                  <div className={styles.subtle}>
                    {item.status}
                    {item.decision_reason ? ` · ${item.decision_reason}` : ''}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </BlockStack>
    </Card>
  );
}
